/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { StatusIconMap, useTheme, SvgMore, SvgNew, SvgCheckmark, LinkAction, useSafeContext, supportsHas, } from '../utils/index.js';
import '@itwin/itwinui-css/css/tile.css';
import { DropdownMenu } from '../DropdownMenu/index.js';
import { IconButton } from '../Buttons/index.js';
import { ProgressRadial } from '../ProgressIndicators/index.js';
const TileContext = React.createContext(undefined);
/**
 * Polymorphic Tile action component. Recommended to be used in a "name" of `Tile`.
 * Renders `a` element by default.
 * @example
 * <Tile
 *   name={<Tile.Action href='/new-page'>Tile name<Tile.Action/>}
 * />
 */
export const TileAction = (props) => {
    const tileContext = useSafeContext(TileContext);
    React.useEffect(() => {
        if (!supportsHas()) {
            tileContext.setActionable(true);
        }
    }, [tileContext]);
    return React.createElement(LinkAction, { ...props });
};
/**
 * Tile component that displays content and actions in a card-like format.
 * @example
 * <Tile
 *  name='Tile name'
 *  description='Tile description that takes upto 3 lines'
 *  metadata={<TagContainer><Tag variant='basic'>Tag 1</Tag></TagContainer>}
 *  thumbnail='/url/to/image.jpg'
 *  badge={<Badge backgroundColor='blue'>Badge label</Badge>}
 *  buttons={[<Button>Button 1</Button>, <Button>Button 2</Button>]}
 *  moreOptions={[<MenuItem>Item 1</MenuItem>, <MenuItem>Item 2</MenuItem>]}
 *  leftIcon={<IconButton><SvgInfo /></IconButton>}
 *  rightIcon={<IconButton><SvgStar /></IconButton>}
 *  isSelected={true}
 *  isNew={false}
 * />
 */
export const Tile = Object.assign((props) => {
    const { className, name, description, metadata, thumbnail, buttons, leftIcon, rightIcon, badge, isNew, isSelected, moreOptions, variant = 'default', children, isActionable: isActionableProp, status, isLoading = false, isDisabled = false, onClick, ...rest } = props;
    useTheme();
    const [isMenuVisible, setIsMenuVisible] = React.useState(false);
    const showMenu = React.useCallback(() => setIsMenuVisible(true), []);
    const hideMenu = React.useCallback(() => setIsMenuVisible(false), []);
    const [localActionable, setLocalActionable] = React.useState(isActionableProp);
    const isActionable = isActionableProp !== null && isActionableProp !== void 0 ? isActionableProp : localActionable;
    const tileName = (React.createElement("div", { className: 'iui-tile-name' },
        React.createElement(TitleIcon, { isLoading: isLoading, isSelected: isSelected, isNew: isNew, status: status }),
        React.createElement("span", { className: 'iui-tile-name-label' }, isActionable && onClick ? (React.createElement(LinkAction, { as: 'button', onClick: !isDisabled ? onClick : undefined, "aria-disabled": isDisabled }, name)) : (name))));
    return (React.createElement(TileContext.Provider, { value: { setActionable: setLocalActionable } },
        React.createElement("div", { className: cx('iui-tile', {
                'iui-folder': variant === 'folder',
                'iui-new': isNew,
                'iui-selected': isSelected,
                'iui-actionable': isActionable,
                [`iui-${status}`]: !!status,
                'iui-loading': isLoading,
            }, className), "aria-disabled": isDisabled, ...rest },
            variant !== 'folder' ? tileName : null,
            thumbnail && (React.createElement("div", { className: 'iui-tile-thumbnail' },
                typeof thumbnail === 'string' ? (React.createElement("div", { className: 'iui-tile-thumbnail-picture', style: { backgroundImage: `url(${thumbnail})` } })) : thumbnail && thumbnail.type === 'img' ? (React.cloneElement(thumbnail, {
                    className: 'iui-tile-thumbnail-picture',
                })) : React.isValidElement(thumbnail) ? (React.cloneElement(thumbnail, {
                    className: cx('iui-thumbnail-icon', thumbnail.props.className),
                })) : (thumbnail),
                leftIcon &&
                    React.cloneElement(leftIcon, {
                        className: 'iui-tile-thumbnail-type-indicator',
                        'data-iui-size': 'small',
                    }),
                rightIcon &&
                    React.cloneElement(rightIcon, {
                        className: 'iui-tile-thumbnail-quick-action',
                        'data-iui-size': 'small',
                    }),
                badge && (React.createElement("div", { className: 'iui-tile-thumbnail-badge-container' }, badge)))),
            React.createElement("div", { className: 'iui-tile-content' },
                variant === 'folder' ? tileName : null,
                description != undefined && (React.createElement("div", { className: 'iui-tile-description' }, description)),
                metadata != undefined && (React.createElement("div", { className: 'iui-tile-metadata' }, metadata)),
                moreOptions && (React.createElement(DropdownMenu, { onShow: showMenu, onHide: hideMenu, menuItems: (close) => moreOptions.map((option) => React.cloneElement(option, {
                        onClick: (value) => {
                            var _a, _b;
                            close();
                            (_b = (_a = option.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, value);
                        },
                    })) },
                    React.createElement("div", { className: cx('iui-tile-more-options', {
                            'iui-visible': isMenuVisible,
                        }) },
                        React.createElement(IconButton, { styleType: 'borderless', size: 'small', "aria-label": 'More options' },
                            React.createElement(SvgMore, null))))),
                children),
            buttons && React.createElement("div", { className: 'iui-tile-buttons' }, buttons))));
}, {
    /**
     * Polymorphic Tile action component. Recommended to be used in a "name" of `Tile`.
     * Renders `a` element by default.
     * @example
     * <Tile
     *   name={<Tile.Action href='/new-page'>Tile name<Tile.Action/>}
     * />
     */
    Action: TileAction,
});
const TitleIcon = ({ isLoading = false, isSelected = false, isNew = false, status, }) => {
    const StatusIcon = !!status && StatusIconMap[status];
    if (isLoading) {
        return (React.createElement(ProgressRadial, { className: 'iui-tile-status-icon', "aria-hidden": true, indeterminate: true }));
    }
    if (isSelected) {
        return React.createElement(SvgCheckmark, { className: 'iui-tile-status-icon', "aria-hidden": true });
    }
    if (isNew) {
        return React.createElement(SvgNew, { className: 'iui-tile-status-icon', "aria-hidden": true });
    }
    if (StatusIcon) {
        return React.createElement(StatusIcon, { className: 'iui-tile-status-icon' });
    }
    return null;
};
export default Tile;
